package com.htmlagent.site.state.chat

import com.htmlagent.site.i18n.AppLocale

/**
 * The shape the skeleton uses to draw a section. If they were all the same rectangle, the
 * skeleton would look like a list loading and not like a page being built.
 */
enum class SectionShape {
    /** A strip of short elements: navigation, footer. */
    BAR,

    /** The large opening block. */
    HERO,

    /** Everything else: a heading and a grid of cards. */
    CARDS,
}

/** A section of the page, as the panel sees it while it is being written. */
data class SectionProgress(
    val id: String,
    val label: String,
    val shape: SectionShape,
    /** Its closing marker already arrived: that part is written in full. */
    val done: Boolean,
)

/**
 * Progress of the page while it is written, read from the `<!-- #id -->` … `<!-- /#id -->`
 * markers the generated HTML already carries: every close is a finished section.
 *
 * The pattern is a copy of the one in `ArtifactSections` in `:domain`, because `:site` can only
 * see `:shared`. If they diverge nothing breaks: no section is recognized and the panel falls
 * back to the generic skeleton. The clean way to drop the copy would be for the backend to emit
 * the finished section as a `StreamEvent`.
 *
 * It is consumed chunk by chunk instead of rereading the whole document, which would be
 * quadratic; an overlap the size of the longest marker rebuilds a marker split across two chunks.
 */
class ArtifactProgress {

    private val opened = mutableListOf<String>()
    private val closed = mutableSetOf<String>()

    /** The tail of the previous chunk, the only thing kept from the past; see [OVERLAP]. */
    private var tail = ""

    /** The page name, as soon as the model writes its `<title>`. */
    var title: String? = null
        private set

    fun reset() {
        opened.clear()
        closed.clear()
        tail = ""
        title = null
    }

    /** Takes in the chunk that just arrived, not the whole document; see the class. */
    fun consume(chunk: String) {
        if (chunk.isEmpty()) return
        val scanWindow = tail + chunk

        OPEN.findAll(scanWindow).forEach { match ->
            val id = match.groupValues[1]
            if (id !in opened) opened += id
        }
        CLOSE.findAll(scanWindow).forEach { match -> closed += match.groupValues[1] }

        if (title == null) {
            TITLE.find(scanWindow)?.groupValues?.get(1)?.trim()?.takeIf { it.isNotBlank() }
                ?.let { title = it }
        }

        tail = scanWindow.takeLast(OVERLAP)
    }

    fun sections(): List<SectionProgress> = opened.map { id ->
        SectionProgress(id = id, label = labelOf(id), shape = shapeOf(id), done = id in closed)
    }

    /** The identifier is the contract with the agent; only its visible name is translated. */
    private fun labelOf(id: String): String = AppLocale.strings.activity.sectionLabel(id)

    private fun shapeOf(id: String): SectionShape = when (id) {
        "nav", "navbar", "header", "cabecera", "footer", "pie" -> SectionShape.BAR
        "hero", "portada" -> SectionShape.HERO
        else -> SectionShape.CARDS
    }

    private companion object {

        val OPEN = Regex("<!--\\s*#([a-z0-9-]+)\\s*-->")
        val CLOSE = Regex("<!--\\s*/#([a-z0-9-]+)\\s*-->")
        val TITLE = Regex("<title[^>]*>([^<]*)</title>", RegexOption.IGNORE_CASE)

        /**
         * What is carried from one chunk to the next. It is sized for a long `<title>`, the
         * widest thing that has to be rebuilt, and not for the markers.
         */
        const val OVERLAP = 200
    }
}
